/**
 * Certificate design templates (F1 designer).
 *
 * Each template is a pure builder: (options) => an SVG string sized 842×595
 * (A4 landscape, in points). The SVG embeds {{merge}} tokens that the server
 * renderer (@common/design-render.mjs) substitutes per recipient, and renders
 * to a vector PDF with pdfkit + svg-to-pdfkit. The SAME SVG is what the client
 * previews (with sample data) and what we persist as the badge's certificate.
 *
 * Typography is a real embedded type system (Phase 0 of the template-quality
 * plan), not the base-14 PDF fonts: an engraved display serif (Cinzel), a
 * formal connecting script for the recipient name (Great Vibes), a book serif
 * (EB Garamond) and a neutral grotesque (Inter). The SAME family names resolve
 * on the server — the renderer embeds the OFL fonts and maps each family via
 * @common/cert-fonts.mjs — and in the browser, where certificateFonts.ts loads
 * them as @font-face, so the live preview matches the PDF. Each family also
 * carries a CSS fallback for any surface where the font hasn't loaded yet.
 *
 * Dynamic per-recipient bits are tokens, not baked values:
 *   {{recipient.name}} {{badge.name}} {{issuer.name}} {{credential.issuedOn}}
 *   {{credential.verifyUrl}}   — text
 *   {{credential.qr}} {{issuer.logo}} — data-URI images injected at render time
 */

export type CertificateTemplateId =
  | 'classic' | 'modern' | 'minimal'
  | 'elegant' | 'corporate' | 'bold' | 'seal' | 'framed' | 'ribbon'
  | 'sidebar' | 'wave' | 'dark' | 'heritage';

export interface CertificateOptions {
  template: CertificateTemplateId;
  accentColor: string;   // hex, e.g. #7c3aed
  title: string;         // e.g. "Certificate of Achievement"
  showQr: boolean;
  showLogo: boolean;
}

export const DEFAULT_CERTIFICATE_OPTIONS: CertificateOptions = {
  template: 'classic',
  accentColor: '#7c3aed',
  title: 'Certificate of Achievement',
  showQr: true,
  showLogo: false,
};

export const CERTIFICATE_TEMPLATES: { id: CertificateTemplateId; name: string }[] = [
  { id: 'classic', name: 'Classic' },
  { id: 'modern', name: 'Modern' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'elegant', name: 'Elegant' },
  { id: 'corporate', name: 'Corporate' },
  { id: 'bold', name: 'Bold' },
  { id: 'seal', name: 'Seal' },
  { id: 'framed', name: 'Framed' },
  { id: 'ribbon', name: 'Ribbon' },
  { id: 'sidebar', name: 'Sidebar' },
  { id: 'wave', name: 'Wave' },
  { id: 'dark', name: 'Dark' },
  { id: 'heritage', name: 'Heritage' },
];

const W = 842;
const H = 595;

// SAFE_INSET — as a fraction of the canvas, the minimum margin the frame and
// content keep from the edge, so nothing crosses the border. Applied uniformly
// off the short side (~30px). See memory: feedback_template_border_margins.
export const SAFE_INSET = 0.05;
const M = Math.round(H * SAFE_INSET);        // outer frame inset (~30px)
const MI = M + 8;                            // inner border ring inset

// The type system. These family names resolve to embedded OFL fonts on the
// server (@common/cert-fonts.mjs) and to @font-face rules in the browser
// (certificateFonts.ts) — preview == PDF. Bold is expressed with
// font-weight="bold" on the element (the server maps it to the bold TTF; the
// browser to the 600/700 face), so there are no separate *_B constants.
const DISPLAY = "'Cinzel', Georgia, 'Times New Roman', serif";           // engraved caps — titles
const SCRIPT = "'Great Vibes', 'Snell Roundhand', 'Segoe Script', cursive"; // formal script — recipient name
const SERIF = "'EB Garamond', Georgia, 'Times New Roman', serif";        // book serif — body
const SANS = "'Inter', Helvetica, Arial, sans-serif";                    // grotesque — modern body/labels
// Back-compat alias: the sans templates express bold via font-weight="bold",
// but many still reference this name for the family.
const SANS_B = SANS;

const INK = '#1b1f2a';
const MUTE = '#6b7280';
const FAINT = '#9aa0ac';
const LINE = '#e6e8ec';

function esc(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** QR block referencing the render-time data-URI token, shown only if enabled. */
function qrBlock(x: number, y: number, size: number, show: boolean): string {
  if (!show) return '';
  return `<image x="${x}" y="${y}" width="${size}" height="${size}" href="{{credential.qr}}" preserveAspectRatio="xMidYMid meet"/>`;
}

/** Optional issuer logo (render-time data URI). */
function logoBlock(x: number, y: number, size: number, show: boolean): string {
  if (!show) return '';
  return `<image x="${x}" y="${y}" width="${size}" height="${size}" href="{{issuer.logo}}" preserveAspectRatio="xMidYMid meet"/>`;
}

function classic(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="${a}" stroke-width="2"/>
  <rect x="${MI}" y="${MI}" width="${W - 2 * MI}" height="${H - 2 * MI}" fill="none" stroke="${a}" stroke-width="0.75"/>
  ${logoBlock(W / 2 - 26, 66, 52, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 168 : 150}" text-anchor="middle" font-family="${DISPLAY}" font-size="30" fill="${INK}" letter-spacing="3">${title}</text>
  <rect x="${W / 2 - 40}" y="${o.showLogo ? 182 : 164}" width="80" height="2" fill="${a}"/>
  <text x="${W / 2}" y="${o.showLogo ? 234 : 222}" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${MUTE}" letter-spacing="3">THIS CERTIFIES THAT</text>
  <text x="${W / 2}" y="${o.showLogo ? 306 : 296}" text-anchor="middle" font-family="${SCRIPT}" font-size="66" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="${o.showLogo ? 350 : 342}" text-anchor="middle" font-family="${SERIF}" font-size="15" fill="${MUTE}">has successfully earned</text>
  <text x="${W / 2}" y="${o.showLogo ? 388 : 382}" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="24" fill="${INK}" data-fit="1">{{badge.name}}</text>
  <text x="150" y="512" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="14" fill="${INK}">{{issuer.name}}</text>
  <rect x="70" y="522" width="160" height="1" fill="${FAINT}"/>
  <text x="150" y="540" text-anchor="middle" font-family="${SANS}" font-size="10" fill="${FAINT}" letter-spacing="1">ISSUER</text>
  <text x="${W - 150}" y="512" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="14" fill="${INK}">{{credential.issuedOn}}</text>
  <rect x="${W - 230}" y="522" width="160" height="1" fill="${FAINT}"/>
  <text x="${W - 150}" y="540" text-anchor="middle" font-family="${SANS}" font-size="10" fill="${FAINT}" letter-spacing="1">DATE ISSUED</text>
  ${qrBlock(W / 2 - 34, 470, 68, o.showQr)}
</svg>`;
}

function modern(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const railW = 26;
  const left = 96;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="0" y="0" width="${railW}" height="${H}" fill="${a}"/>
  <rect x="${railW}" y="0" width="4" height="${H}" fill="${INK}" opacity="0.08"/>
  ${logoBlock(left, 70, 56, o.showLogo)}
  <text x="${left}" y="${o.showLogo ? 176 : 138}" font-family="${SANS_B}" font-weight="bold" font-size="34" fill="${INK}">${title}</text>
  <rect x="${left}" y="${o.showLogo ? 196 : 158}" width="64" height="4" fill="${a}"/>
  <text x="${left}" y="${o.showLogo ? 268 : 244}" font-family="${SANS}" font-size="13" fill="${MUTE}" letter-spacing="2">PRESENTED TO</text>
  <text x="${left}" y="${o.showLogo ? 322 : 300}" font-family="${SANS_B}" font-weight="bold" font-size="48" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${left}" y="${o.showLogo ? 372 : 352}" font-family="${SANS}" font-size="15" fill="${INK}">For earning <tspan font-family="${SANS_B}" font-weight="bold">{{badge.name}}</tspan></text>
  <text x="${left}" y="512" font-family="${SANS_B}" font-weight="bold" font-size="13" fill="${INK}">{{issuer.name}}</text>
  <text x="${left}" y="532" font-family="${SANS}" font-size="11" fill="${FAINT}">Issued {{credential.issuedOn}}</text>
  ${qrBlock(W - 118, H - 118, 72, o.showQr)}
</svg>`;
}

function minimal(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="0" y="0" width="${W}" height="8" fill="${a}"/>
  ${logoBlock(W / 2 - 22, 90, 44, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 196 : 176}" text-anchor="middle" font-family="${SANS}" font-size="15" fill="${MUTE}" letter-spacing="4">${title.toUpperCase()}</text>
  <text x="${W / 2}" y="${o.showLogo ? 286 : 276}" text-anchor="middle" font-family="${SANS_B}" font-weight="bold" font-size="50" fill="${INK}" data-fit="1">{{recipient.name}}</text>
  <rect x="${W / 2 - 30}" y="${o.showLogo ? 312 : 302}" width="60" height="3" fill="${a}"/>
  <text x="${W / 2}" y="${o.showLogo ? 366 : 358}" text-anchor="middle" font-family="${SANS}" font-size="16" fill="${MUTE}" data-fit="1">{{badge.name}}</text>
  <text x="${W / 2}" y="470" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${FAINT}">{{issuer.name}} · {{credential.issuedOn}}</text>
  ${qrBlock(W / 2 - 30, 492, 60, o.showQr)}
</svg>`;
}

function elegant(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#fffdf9"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="${a}" stroke-width="1.5"/>
  <rect x="${MI}" y="${MI}" width="${W - 2 * MI}" height="${H - 2 * MI}" fill="none" stroke="${a}" stroke-width="0.5"/>
  <circle cx="${W / 2}" cy="96" r="20" fill="none" stroke="${a}" stroke-width="1.5"/>
  <circle cx="${W / 2}" cy="96" r="13" fill="${a}" opacity="0.12"/>
  ${logoBlock(W / 2 - 16, 80, 32, o.showLogo)}
  <text x="${W / 2}" y="176" text-anchor="middle" font-family="${DISPLAY}" font-size="17" fill="${MUTE}" letter-spacing="5">${title}</text>
  <rect x="${W / 2 - 50}" y="192" width="100" height="1" fill="${a}"/>
  <text x="${W / 2}" y="240" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${FAINT}" letter-spacing="3">PRESENTED TO</text>
  <text x="${W / 2}" y="308" text-anchor="middle" font-family="${SCRIPT}" font-size="68" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="352" text-anchor="middle" font-family="${SERIF}" font-size="15" fill="${MUTE}" font-style="italic">in recognition of</text>
  <text x="${W / 2}" y="388" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="23" fill="${INK}" data-fit="1">{{badge.name}}</text>
  <text x="${W / 2}" y="520" text-anchor="middle" font-family="${SERIF}" font-size="13" fill="${MUTE}">{{issuer.name}} &#183; {{credential.issuedOn}}</text>
  ${qrBlock(W / 2 - 30, 452, 60, o.showQr)}
</svg>`;
}

function corporate(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const band = 96;
  const left = 72;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="0" y="0" width="${W}" height="${band}" fill="${a}"/>
  ${logoBlock(W - 148, 24, 48, o.showLogo)}
  <text x="${left}" y="60" font-family="${SANS_B}" font-weight="bold" font-size="28" fill="#ffffff">${title}</text>
  <text x="${left}" y="${band + 66}" font-family="${SANS}" font-size="13" fill="${MUTE}" letter-spacing="2">THIS CERTIFIES THAT</text>
  <text x="${left}" y="${band + 128}" font-family="${SANS_B}" font-weight="bold" font-size="44" fill="${INK}" data-fit="1">{{recipient.name}}</text>
  <text x="${left}" y="${band + 172}" font-family="${SANS}" font-size="15" fill="${MUTE}">has successfully completed <tspan font-family="${SANS_B}" font-weight="bold" fill="${INK}">{{badge.name}}</tspan></text>
  <rect x="${left}" y="500" width="${W - 2 * left}" height="1" fill="${LINE}"/>
  <text x="${left}" y="528" font-family="${SANS_B}" font-weight="bold" font-size="12" fill="${INK}">{{issuer.name}}</text>
  <text x="${left}" y="546" font-family="${SANS}" font-size="10" fill="${FAINT}" letter-spacing="1">ISSUER</text>
  <text x="${W - left}" y="528" text-anchor="end" font-family="${SANS_B}" font-weight="bold" font-size="12" fill="${INK}">{{credential.issuedOn}}</text>
  <text x="${W - left}" y="546" text-anchor="end" font-family="${SANS}" font-size="10" fill="${FAINT}" letter-spacing="1">DATE</text>
  ${qrBlock(W - 148, 400, 64, o.showQr)}
</svg>`;
}

function bold(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const left = 80;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <polygon points="0,0 260,0 0,260" fill="${a}"/>
  <polygon points="${W},${H} ${W - 200},${H} ${W},${H - 200}" fill="${a}" opacity="0.12"/>
  ${logoBlock(W - 140, 48, 52, o.showLogo)}
  <text x="${left}" y="212" font-family="${SANS}" font-weight="bold" font-size="20" fill="${a}" letter-spacing="3">${title.toUpperCase()}</text>
  <text x="${left}" y="300" font-family="${SANS}" font-weight="bold" font-size="60" fill="${INK}" data-fit="1">{{recipient.name}}</text>
  <rect x="${left}" y="320" width="120" height="6" fill="${a}"/>
  <text x="${left}" y="378" font-family="${SANS}" font-size="17" fill="${MUTE}">earned <tspan font-family="${SANS}" font-weight="bold" fill="${INK}">{{badge.name}}</tspan></text>
  <text x="${left}" y="524" font-family="${SANS_B}" font-weight="bold" font-size="13" fill="${INK}">{{issuer.name}}</text>
  <text x="${left}" y="544" font-family="${SANS}" font-size="11" fill="${FAINT}">Issued {{credential.issuedOn}}</text>
  ${qrBlock(W - 128, H - 128, 76, o.showQr)}
</svg>`;
}

function seal(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const cx = W - 132, cy = 470;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="0" y="0" width="${W}" height="6" fill="${a}"/>
  <rect x="0" y="${H - 6}" width="${W}" height="6" fill="${a}"/>
  ${logoBlock(W / 2 - 20, 64, 40, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 158 : 138}" text-anchor="middle" font-family="${DISPLAY}" font-size="30" fill="${INK}" letter-spacing="3">${title}</text>
  <rect x="${W / 2 - 44}" y="${o.showLogo ? 174 : 154}" width="88" height="2" fill="${a}"/>
  <text x="${W / 2}" y="${o.showLogo ? 224 : 210}" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${MUTE}" letter-spacing="3">AWARDED TO</text>
  <text x="${W / 2}" y="${o.showLogo ? 296 : 286}" text-anchor="middle" font-family="${SCRIPT}" font-size="64" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="${o.showLogo ? 344 : 336}" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="20" fill="${INK}" data-fit="1">{{badge.name}}</text>
  <!-- seal -->
  <circle cx="${cx}" cy="${cy}" r="46" fill="none" stroke="${a}" stroke-width="2"/>
  <circle cx="${cx}" cy="${cy}" r="38" fill="${a}" opacity="0.10"/>
  <circle cx="${cx}" cy="${cy}" r="38" fill="none" stroke="${a}" stroke-width="0.75"/>
  <path d="M ${cx - 16} ${cy} l 10 11 l 20 -22" fill="none" stroke="${a}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  <text x="72" y="516" font-family="${SANS_B}" font-weight="bold" font-size="13" fill="${INK}">{{issuer.name}}</text>
  <text x="72" y="536" font-family="${SANS}" font-size="11" fill="${FAINT}">Issued {{credential.issuedOn}}</text>
  ${qrBlock(72, 430, 60, o.showQr)}
</svg>`;
}

function framed(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="${a}" stroke-width="10"/>
  <rect x="${M + 18}" y="${M + 18}" width="${W - 2 * (M + 18)}" height="${H - 2 * (M + 18)}" fill="none" stroke="${INK}" stroke-width="1"/>
  <rect x="${M}" y="${M}" width="28" height="28" fill="${a}"/>
  <rect x="${W - M - 28}" y="${M}" width="28" height="28" fill="${a}"/>
  <rect x="${M}" y="${H - M - 28}" width="28" height="28" fill="${a}"/>
  <rect x="${W - M - 28}" y="${H - M - 28}" width="28" height="28" fill="${a}"/>
  ${logoBlock(W / 2 - 18, 78, 36, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 172 : 152}" text-anchor="middle" font-family="${DISPLAY}" font-size="32" fill="${INK}" letter-spacing="3">${title}</text>
  <text x="${W / 2}" y="${o.showLogo ? 224 : 210}" text-anchor="middle" font-family="${SERIF}" font-size="14" fill="${MUTE}" font-style="italic">is hereby granted to</text>
  <text x="${W / 2}" y="${o.showLogo ? 300 : 290}" text-anchor="middle" font-family="${SCRIPT}" font-size="64" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="${o.showLogo ? 348 : 340}" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="19" fill="${INK}" data-fit="1">{{badge.name}}</text>
  <text x="${W / 2}" y="512" text-anchor="middle" font-family="${SERIF}" font-size="13" fill="${MUTE}">{{issuer.name}} &#183; {{credential.issuedOn}}</text>
  ${qrBlock(W / 2 - 28, 446, 56, o.showQr)}
</svg>`;
}

function ribbon(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const bw = 420, bx = (W - bw) / 2, by = 96, bh = 56;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="${a}" stroke-width="1" opacity="0.4"/>
  <!-- banner ribbon -->
  <polygon points="${bx - 24},${by + 8} ${bx},${by} ${bx},${by + bh} ${bx - 24},${by + bh + 8} ${bx - 12},${by + bh / 2 + 4}" fill="${a}" opacity="0.55"/>
  <polygon points="${bx + bw + 24},${by + 8} ${bx + bw},${by} ${bx + bw},${by + bh} ${bx + bw + 24},${by + bh + 8} ${bx + bw + 12},${by + bh / 2 + 4}" fill="${a}" opacity="0.55"/>
  <rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="4" fill="${a}"/>
  <text x="${W / 2}" y="${by + bh / 2 + 7}" text-anchor="middle" font-family="${DISPLAY}" font-size="19" fill="#ffffff" letter-spacing="3">${title}</text>
  ${logoBlock(W / 2 - 16, 190, 32, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 262 : 250}" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${MUTE}" letter-spacing="3">AWARDED TO</text>
  <text x="${W / 2}" y="${o.showLogo ? 328 : 320}" text-anchor="middle" font-family="${SCRIPT}" font-size="60" fill="${INK}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="${o.showLogo ? 370 : 364}" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="18" fill="${a}" data-fit="1">{{badge.name}}</text>
  <text x="${W / 2}" y="522" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${FAINT}">{{issuer.name}} &#183; {{credential.issuedOn}}</text>
  ${qrBlock(W / 2 - 28, 448, 56, o.showQr)}
</svg>`;
}

// Certifier's most-used structure: a full-height left color panel, left-aligned
// body on white. Corporate/modern.
function sidebar(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const pw = 264, x0 = 300;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <rect x="0" y="0" width="${pw}" height="${H}" fill="${a}"/>
  <rect x="${pw}" y="0" width="4" height="${H}" fill="${INK}" opacity="0.08"/>
  ${o.showLogo ? logoBlock(56, 64, 64, true) : `<rect x="56" y="70" width="56" height="6" fill="#ffffff" opacity="0.9"/>`}
  <rect x="56" y="${H - 150}" width="150" height="1" fill="#ffffff" opacity="0.35"/>
  <text x="56" y="${H - 120}" font-family="${SANS}" font-size="11" fill="#ffffff" opacity="0.85" letter-spacing="2">VERIFIED CREDENTIAL</text>
  <text x="${x0}" y="130" font-family="${SANS_B}" font-weight="bold" font-size="26" fill="${a}" letter-spacing="1">${title.toUpperCase()}</text>
  <rect x="${x0}" y="148" width="60" height="4" fill="${a}"/>
  <text x="${x0}" y="222" font-family="${SANS}" font-size="13" fill="${MUTE}" letter-spacing="2">THIS CERTIFIES THAT</text>
  <text x="${x0}" y="284" font-family="${SANS_B}" font-weight="bold" font-size="44" fill="${INK}" data-fit="1">{{recipient.name}}</text>
  <text x="${x0}" y="332" font-family="${SANS}" font-size="15" fill="${MUTE}">has earned <tspan font-family="${SANS_B}" font-weight="bold" fill="${INK}">{{badge.name}}</tspan></text>
  <text x="${x0}" y="512" font-family="${SANS_B}" font-weight="bold" font-size="13" fill="${INK}">{{issuer.name}}</text>
  <text x="${x0}" y="532" font-family="${SANS}" font-size="11" fill="${FAINT}">Issued {{credential.issuedOn}}</text>
  ${qrBlock(W - 122, H - 122, 72, o.showQr)}
</svg>`;
}

// Certifier's single most-clicked individual template: soft curved wave bands.
function wave(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <path d="M0,0 H${W} V44 C ${W * 0.62},96 ${W * 0.34},-8 0,52 Z" fill="${a}" opacity="0.35"/>
  <path d="M0,0 H${W} V70 C ${W * 0.66},128 ${W * 0.32},18 0,80 Z" fill="${a}"/>
  <path d="M0,${H} H${W} V${H - 44} C ${W * 0.62},${H - 96} ${W * 0.34},${H + 8} 0,${H - 52} Z" fill="${a}" opacity="0.35"/>
  <path d="M0,${H} H${W} V${H - 70} C ${W * 0.66},${H - 128} ${W * 0.32},${H - 18} 0,${H - 80} Z" fill="${a}"/>
  ${logoBlock(W / 2 - 16, 104, 32, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 188 : 170}" text-anchor="middle" font-family="${SANS}" font-weight="bold" font-size="17" fill="${MUTE}" letter-spacing="3">${title.toUpperCase()}</text>
  <text x="${W / 2}" y="${o.showLogo ? 228 : 218}" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${MUTE}" letter-spacing="3">PRESENTED TO</text>
  <text x="${W / 2}" y="${o.showLogo ? 300 : 294}" text-anchor="middle" font-family="${SCRIPT}" font-size="60" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="${o.showLogo ? 348 : 344}" text-anchor="middle" font-family="${SANS}" font-weight="bold" font-size="16" fill="${INK}" data-fit="1">{{badge.name}}</text>
  <text x="${W / 2}" y="430" text-anchor="middle" font-family="${SANS}" font-size="12" fill="${FAINT}">{{issuer.name}} &#183; {{credential.issuedOn}}</text>
  ${qrBlock(W / 2 - 23, 444, 46, o.showQr)}
</svg>`;
}

// Premium dark background, light text. Fixed dark ground (independent of accent
// so it always reads); accent used for rules + the credential chip.
function dark(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  const BG = "#131a2b", LT = "#f5f7fa", LTM = "#9aa4bd";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="${BG}"/>
  <rect x="0" y="0" width="${W}" height="6" fill="${a}"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="#ffffff" stroke-width="0.5" opacity="0.12"/>
  ${logoBlock(W / 2 - 18, 74, 36, o.showLogo)}
  <text x="${W / 2}" y="${o.showLogo ? 184 : 166}" text-anchor="middle" font-family="${SANS}" font-size="14" fill="${LTM}" letter-spacing="5">${title.toUpperCase()}</text>
  <text x="${W / 2}" y="${o.showLogo ? 286 : 278}" text-anchor="middle" font-family="${SCRIPT}" font-size="66" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <rect x="${W / 2 - 30}" y="${o.showLogo ? 306 : 298}" width="60" height="3" fill="${a}"/>
  <text x="${W / 2}" y="${o.showLogo ? 356 : 348}" text-anchor="middle" font-family="${SANS}" font-size="16" fill="#c7cede" data-fit="1">{{badge.name}}</text>
  <text x="72" y="524" font-family="${SANS_B}" font-weight="bold" font-size="13" fill="${LT}">{{issuer.name}}</text>
  <text x="72" y="544" font-family="${SANS}" font-size="11" fill="${LTM}">Issued {{credential.issuedOn}}</text>
  ${qrBlock(W - 120, H - 120, 68, o.showQr)}
</svg>`;
}

// Warm heritage: cream ground, ornate double border, serif, an emblem, and
// signature-style ISSUER / DATE columns with rules.
function heritage(o: CertificateOptions): string {
  const a = esc(o.accentColor);
  const title = esc(o.title);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#fbf7ef"/>
  <rect x="${M}" y="${M}" width="${W - 2 * M}" height="${H - 2 * M}" fill="none" stroke="${a}" stroke-width="2"/>
  <rect x="${MI}" y="${MI}" width="${W - 2 * MI}" height="${H - 2 * MI}" fill="none" stroke="${a}" stroke-width="0.5"/>
  <rect x="${M + 4}" y="${M + 4}" width="16" height="16" fill="${a}"/>
  <rect x="${W - M - 20}" y="${M + 4}" width="16" height="16" fill="${a}"/>
  <rect x="${M + 4}" y="${H - M - 20}" width="16" height="16" fill="${a}"/>
  <rect x="${W - M - 20}" y="${H - M - 20}" width="16" height="16" fill="${a}"/>
  <circle cx="${W / 2}" cy="92" r="19" fill="none" stroke="${a}" stroke-width="1.5"/>
  <path d="M ${W / 2} 80 l 3.4 7 l 7.6 0.6 l -5.8 5 l 1.8 7.4 l -7 -4 l -7 4 l 1.8 -7.4 l -5.8 -5 l 7.6 -0.6 Z" fill="${a}"/>
  ${logoBlock(W / 2 - 14, 78, 28, o.showLogo)}
  <text x="${W / 2}" y="168" text-anchor="middle" font-family="${DISPLAY}" font-size="18" fill="${MUTE}" letter-spacing="5">${title}</text>
  <rect x="${W / 2 - 55}" y="184" width="110" height="1" fill="${a}"/>
  <text x="${W / 2}" y="230" text-anchor="middle" font-family="${SERIF}" font-size="14" fill="${FAINT}" font-style="italic">is proudly presented to</text>
  <text x="${W / 2}" y="298" text-anchor="middle" font-family="${SCRIPT}" font-size="66" fill="${a}" data-fit="1">{{recipient.name}}</text>
  <text x="${W / 2}" y="340" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="19" fill="${INK}" data-fit="1">{{badge.name}}</text>
  ${qrBlock(W / 2 - 24, 366, 48, o.showQr)}
  <rect x="188" y="492" width="180" height="1" fill="${FAINT}"/>
  <text x="278" y="486" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="14" fill="${INK}">{{issuer.name}}</text>
  <text x="278" y="508" text-anchor="middle" font-family="${SANS}" font-size="9" fill="${FAINT}" letter-spacing="1">ISSUER</text>
  <rect x="${W - 368}" y="492" width="180" height="1" fill="${FAINT}"/>
  <text x="${W - 278}" y="486" text-anchor="middle" font-family="${SERIF}" font-weight="bold" font-size="14" fill="${INK}">{{credential.issuedOn}}</text>
  <text x="${W - 278}" y="508" text-anchor="middle" font-family="${SANS}" font-size="9" fill="${FAINT}" letter-spacing="1">DATE</text>
</svg>`;
}

const BUILDERS: Record<CertificateTemplateId, (o: CertificateOptions) => string> = {
  classic,
  modern,
  minimal,
  elegant,
  corporate,
  bold,
  seal,
  framed,
  ribbon,
  sidebar,
  wave,
  dark,
  heritage,
};

/** Build the certificate SVG (with {{tokens}}) for the given options. The
 * options are embedded as a data attribute on the root <svg> so the designer
 * can re-open and edit an existing certificate. The server renderer ignores it. */
export function buildCertificateSvg(options: CertificateOptions): string {
  const svg = (BUILDERS[options.template] ?? classic)(options);
  const attr = `data-cert-options='${esc(JSON.stringify(options)).replace(/'/g, '&apos;')}'`;
  return svg.replace(/^<svg /, `<svg ${attr} `);
}

/** Recover the options embedded in a saved certificate SVG, or null. */
export function parseCertificateOptions(svg: string | null | undefined): CertificateOptions | null {
  if (!svg) return null;
  const m = svg.match(/data-cert-options='([^']*)'/);
  if (!m) return null;
  try {
    const json = m[1]
      .replace(/&apos;/g, "'").replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
    const o = JSON.parse(json);
    return { ...DEFAULT_CERTIFICATE_OPTIONS, ...o };
  } catch {
    return null;
  }
}

/** Sample merge values for the live preview. */
export interface CertificateSampleData {
  recipientName?: string;
  badgeName?: string;
  issuerName?: string;
  issuedOn?: string;
  qrDataUri?: string;
  logoDataUri?: string;
}

/**
 * Substitute {{tokens}} with sample values for previewing. Mirrors the server's
 * substitution (XML-escaped, unknown tokens → empty).
 */
export function fillCertificatePreview(svg: string, sample: CertificateSampleData): string {
  const map: Record<string, string> = {
    'recipient.name': sample.recipientName ?? 'Alex Morgan',
    'badge.name': sample.badgeName ?? 'Advanced Certification',
    'issuer.name': sample.issuerName ?? 'Your Organization',
    'credential.issuedOn': sample.issuedOn ?? 'January 1, 2026',
    'credential.verifyUrl': 'https://badges.ninja/verify/sample',
    'credential.qr': sample.qrDataUri ?? '',
    'issuer.logo': sample.logoDataUri ?? '',
  };
  return svg.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) => {
    const v = Object.prototype.hasOwnProperty.call(map, key) ? map[key] : '';
    return String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  });
}
